import "./styles.scss";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import { useDashboard } from "context/admin/dashboard";
import { useTimeTracking } from "context/time-tracking";

import Table from "components/Table";
import TaskInfo from "components/TaskInfo";

import task_status_data from "libs/task_status_data.json";
import task_priority_data from "libs/task_priority_data.json";

const mainClass = "dashboard-admin-deadline-tasks-table";

const DeadlineTasksTable = ({ setWebsiteInfoId, setExplanation }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, filters, filterHandler, loading, updateTask, refetchTasks } =
    useDashboard();
  const { current, timeTracking } = useTimeTracking();

  const table_data = {
    keys: [
      { key: "created_at", value: t("Creation date") },
      { key: "website", value: t("Website [SHORT]") },
      { sort_key: "title", key: "title-full", value: t("Title") },
      { key: "status", value: t("Status") },
      { key: "priority", value: t("Priority") },
      { key: "due_date", value: t("Due date [ALT]") },
      { key: "total_time", value: t("Work time") },
      { key: "details", sort_key: false, value: t("Details") },
    ],
    items: data?.tasks_upcoming_date?.map(
      ({
        _id,
        created_at,
        title,
        website,
        website_id,
        status,
        priority,
        due_date,
        total_time,
      }) => {
        const status_data = task_status_data?.find(
          (item) => item?.value === (status ?? 1),
        );
        const priority_data = task_priority_data?.find(
          (item) => item?.value === (priority ?? 1),
        );

        return [
          {
            key: "created_at",
            type: "text",
            value: moment(created_at).format("LL"),
          },
          {
            key: "website",
            type: "clickable_text",
            value: {
              label: website,
              icon: false,
              onclick: () => setWebsiteInfoId(website_id),
            },
          },
          {
            key: "title-full",
            type: "link",
            value: { label: title, href: `/tasks/${_id}` },
          },
          {
            key: "status",
            type: "pill",
            value: {
              data: {
                value: status_data?.value,
                label: t(status_data?.label),
                color: status_data?.color,
              },
              options: task_status_data
                .filter((item) => item?.key !== "canceled")
                .map((item) => ({ ...item, label: t(item.label) })),
              optionsFooter: (
                <TaskInfo onClick={() => setExplanation("status")} />
              ),
              isCompact: true,
              onChange: (option) =>
                updateTask(_id, { status: option.value }, ["admin_dashboard"]),
            },
          },
          {
            key: "priority",
            type: "pill",
            value: {
              data: {
                value: priority_data?.value,
                label: t(priority_data?.label),
                color: priority_data?.color,
              },
              icon: "flag",
              options: task_priority_data.map((item) => ({
                ...item,
                label: t(item.label),
              })),
              isCompact: true,
              optionsFooter: (
                <TaskInfo
                  type="priority"
                  onClick={() => setExplanation("priority")}
                />
              ),
              onChange: (option) => updateTask(_id, { priority: option.value }),
            },
          },
          {
            key: "due_date",
            type: "due_date",
            value: {
              due_date,
              onChange: async (date) => {
                await updateTask(_id, { due_date: date });
                refetchTasks();
              },
            },
          },
          {
            key: "total_time",
            type: "time_tracker",
            value: {
              label: total_time,
              start_time_tracked:
                current?.tasks?.[0]?._id === _id ? current?.start : null,
              onclick: () => timeTracking(_id, refetchTasks),
            },
          },
          {
            key: "details",
            type: "clickable_text",
            value: {
              label: t("View"),
              onclick: () => navigate(`/tasks/${_id}`),
            },
          },
        ];
      },
    ),
  };

  return (
    <div className={mainClass}>
      <Table
        data={table_data}
        loading={!!loading}
        sort={filters?.sort_deadline}
        setSort={(value) => filterHandler("sort_deadline", value)}
      />
    </div>
  );
};

export default DeadlineTasksTable;
